import { useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import request from 'lib/request';
import { toastAtom } from 'pages/type-curves/atoms';
import { tcInventoryFoucsGateAtom } from 'pages/type-curves/type-curve-filters/atoms';

// Shape of a successful `POST /inventory_workspaces/:id/foucs_exclusions`. Any of
// these three bodies comes back 200 (a promoted or unchanged workspace still
// returns 200), so success alone opens the gate — the body itself is unused here.
interface FoucsExclusionsResponse {
  refreshed: boolean;
  reason?: string;
  exclusionsWritten?: number;
}

/**
 * Inventory Workspace only: after the workspace GET resolves, force a FOUCS
 * exclusions recompute (`POST .../foucs_exclusions` with `{ force: true }`) and
 * drive `tcInventoryFoucsGateAtom` so the map's development-areas and
 * slot-geometries layers only fetch once it returns 200.
 *
 * Fires once per workspace id — guarded by a ref so the show atom's keep-old-data
 * refetches (verdict rollups) don't re-fire it — and only after `workspaceLoaded`
 * is true, so it runs strictly after the GET. Resets the gate to `null` on unmount
 * so plain Type Curve pages stay ungated.
 *
 * @param {string | undefined} id - The workspace id from the route.
 * @param {boolean} workspaceLoaded - Whether the workspace GET has resolved.
 * @return {void}
 */
export const useFoucsExclusionsGate = (
  id: string | undefined,
  workspaceLoaded: boolean
): void => {
  const setGate = useSetAtom(tcInventoryFoucsGateAtom);
  const setToast = useSetAtom(toastAtom);
  const firedForId = useRef<string | null>(null);

  useEffect(() => {
    if (!id || !workspaceLoaded) return;
    if (firedForId.current === id) return;
    firedForId.current = id;

    const abortController = new AbortController();
    setGate('pending');

    request
      .post<FoucsExclusionsResponse, unknown>(
        `/inventory_workspaces/${id}/foucs_exclusions`,
        { force: true },
        abortController
      )
      .then((res) => {
        if (abortController.signal.aborted) return;
        if (res.type === 'success') {
          setGate('ready');
          return;
        }
        // Non-200 or transport failure: hold the gated layers and surface it.
        setGate('error');
        setToast({
          kind: 'error',
          title: 'Unable to prepare inventory exclusions',
          subtitle: 'The map may be missing excluded units. Please reload.',
        });
      });

    return () => abortController.abort();
  }, [id, workspaceLoaded, setGate, setToast]);

  // Reset the gate on unmount so leaving the Inventory Workspace page returns the
  // shared map layers to their ungated (plain Type Curve) behaviour.
  useEffect(() => () => setGate(null), [setGate]);
};
